'use client';
import { Box, Grid, Stack, useTheme } from '@mui/material';
import { ProjectCarouselType } from '../type';
import SliderImage from '../../../gallery/SliderImage';
import { useState } from 'react';
import Counter from './Counter';
import Titles from './Titles';
import Descriptions from './Descriptions';
import Selector from './Selector';
import { trackEvent } from '../../../../utils/trackEvent';
import DarkThemeProvider from '../../../../providers/DarkThemeProvider';
import LightThemeProvider from '../../../../providers/LightThemeProvider';
import getRichTextSummary from '../../../RichText/getRichTextSummary';

type Props = {
  data: ProjectCarouselType;
};

const VerticalProjectCarousel = ({ data }: Props) => {
  const { flip, projects, styling } = data;
  const theme = useTheme();
  const [selected, setSelected] = useState<number>(0);
  const element = projects[selected];
  if (!element) return null;
  const shouldFlipDesktop = ['desktop', 'all'].includes(flip!);
  const shouldFlipMobile = ['mobile', 'all'].includes(flip!);
  const ThemeProvider = styling.isDark ? DarkThemeProvider : LightThemeProvider;
  return (
    <ThemeProvider>
      <Stack
        spacing={0.5}
        direction={{
          md: shouldFlipDesktop ? 'column-reverse' : 'column',
          xs: shouldFlipMobile ? 'column-reverse' : 'column',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            aspectRatio: {
              xs: 16 / 9,
              xl: 2,
            },
          }}
        >
          {projects.map(({ id, images }, index) => {
            const isSelected = selected === index;
            const image = images?.[0];
            return (
              <SliderImage
                key={id}
                beforeImage={image?.before}
                afterImage={image?.after}
                sx={{
                  zIndex: isSelected ? 2 : 1,
                  opacity: isSelected ? 1 : 0,
                  transition: theme.transitions.create('opacity', {
                    duration: 300,
                  }),
                  inset: 0,
                  position: 'absolute',
                }}
              />
            );
          })}
        </Box>

        <Box
          sx={{
            borderRadius: { xs: 0.5, md: 1 },
            py: { xs: 3, md: 8 },
            px: { xs: 2, md: 8 },
            bgcolor: 'background.paper',
          }}
        >
          <Grid container spacing={2} rowSpacing={{ xs: 3, md: 2 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={2}>
                <Counter current={selected} max={projects.length} />
                <Titles
                  title={element?.title}
                  subtitle={element?.subtitle}
                  sx={{
                    minHeight: '92px',
                  }}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Descriptions
                description={element.description}
                sx={{
                  height: { xs: '130px', md: '220px', lg: '170px' },
                  overflowY: 'auto',
                }}
              />
            </Grid>
            <Grid size={12} sx={{ mt: 4 }}>
              <Stack direction={'row'} sx={{ justifyContent: 'space-between' }}>
                <Selector
                  value={selected}
                  onChange={(index) => {
                    trackEvent('select_content', {
                      content_type: 'general_interaction',
                      placement: 'projectCarousel',
                      item_id: getRichTextSummary(projects[index]?.title),
                    });
                    setSelected(index);
                  }}
                  isFirst={selected === 0}
                  isLast={selected === projects.length - 1}
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </ThemeProvider>
  );
};

export default VerticalProjectCarousel;
